import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom } from 'jotai';
import values from 'lodash/values';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePopper } from 'react-popper';
import { useNavigate } from 'react-router-dom';
import { createReportSuccessDialogAtom, createReportOverviewDialogAtom } from '../atoms';
import { projectAtom } from '../atoms/projectAtom';
import {
  Checkbox,
  CreateReportSuccessDialog,
  CreateReportOverviewDialog,
  FileInput,
  FilePreview,
  Input,
  RichText,
  Select,
  TextArea,
} from '../components';
import { BROWSERS, REPORT_ENV, REPORT_PLATFORM, REPORT_PRIORITY } from '../lib/constants';
import { NewReportForm, NewReportSchema } from '../lib/schemas';
import { useCreateReportMutation } from '../lib/queries';
import { PATHS } from '../lib/paths';
import { mutationLoadingAtom } from '../atoms/mutationLoadingAtom';

export default function NewReportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, setCreateReportSuccessDialogState] = useAtom(createReportSuccessDialogAtom);
  const [, setCreateReportOverviewDialogState] = useAtom(createReportOverviewDialogAtom);

  const [project] = useAtom(projectAtom);
  const { register, watch, handleSubmit, setValue, formState, reset, resetField } =
    useForm<NewReportForm>({
      mode: 'all',
      defaultValues: {
        name: '',
        env: REPORT_ENV.DEV,
        platform: REPORT_PLATFORM.WEB,
        priority: REPORT_PRIORITY.MED,
        browsers: [],
        currentDescription: '',
        reproduce: '<p></p>',
        currentScreenshots: [],
        expectedDescription: '',
        expectedScreenshots: [],
      },
      resolver: zodResolver(NewReportSchema),
    });

  const [, setDefaultDescription] = useState<string>('');
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const [popper, setPopper] = useState<HTMLDivElement | null>(null);
  const [showHint, setShowHint] = useState<boolean>(false);
  const { styles, attributes } = usePopper(trigger, popper, { placement: 'bottom-start' });
  const [mutationLoading] = useAtom(mutationLoadingAtom);

  const name = watch('name');
  const env = watch('env');
  const currentDescription = watch('currentDescription');
  const currentScreenshots = watch('currentScreenshots');
  const expectedDescription = watch('expectedDescription');
  const expectedScreenshots = watch('expectedScreenshots');
  const device = watch('device');
  const version = watch('version');
  const otherBrowsers = watch('otherBrowsers');
  const platform = watch('platform');
  const browsers = watch('browsers');
  const reproduce = watch('reproduce');

  const createReportMutation = useCreateReportMutation();

  const fillDescription = () => {
    // eslint-disable-next-line no-restricted-syntax
    const descriptionSummary = `<h2>
            <strong>${name}:</strong>
        </h2>

        <p><strong>${t('report.env')}:</strong></p>
        <ul>
          <li>
             <p>${env}</p>
          </li>
        </ul>
    
        
        <p><strong>${t('report.platform')}:</strong></p>
        <ul>
          <li> <p>${
            platform === REPORT_PLATFORM.MOBILE
              ? `${device}<p>${version}<p>`
              : [...browsers!, otherBrowsers].filter(Boolean).join(', ')
          }</p> </li>
        </ul>
           
        <p><strong>${t('report.currentBehavior')}:</strong></p>
          <ul>
           <li><p>${currentDescription}</p></li>
          </ul>
           
        <p><strong>${t('report.expectedBehavior')}:</strong></p>
          <ul>
            <li><p>${expectedDescription}</p></li>
          </ul>
        <p><strong>${t('report.reproduce')}:</strong></p>
           
        ${reproduce}`;

    setValue('description', descriptionSummary.toString());
    setDefaultDescription(descriptionSummary.toString());
  };

  useEffect(
    () => {
      setCreateReportOverviewDialogState({ isOpen: false, report: undefined });
      setCreateReportSuccessDialogState({ isOpen: false, report: undefined });
      return () => reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  if (!project) return null;

  const saveReport = async (data: NewReportForm) => {
    if (mutationLoading) return;
    await createReportMutation.mutateAsync(
      { ...data, project: project!.id },
      {
        onSuccess: ({ report }) => {
          setCreateReportSuccessDialogState({
            isOpen: true,
            report,
          });
        },
        onError: (error) => {
          const err = error as Error;
          navigate(PATHS.errorPath(err.message));
        },
      },
    );
  };

  const onSubmit = (data: NewReportForm) => {
    setCreateReportOverviewDialogState({
      isOpen: true,
      report: data,
    });
  };

  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold">{t('report.title')}</h1>
      <h3 className="text-title font-semibold text-primary-500">{project.name}</h3>
      <form
        encType="multipart/form-data"
        className="grid gap-2 mt-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h3 className="font-bold text-title pt-2">{t('report.generalInfo')}</h3>
        <Input
          label="report.name"
          type="text"
          disabled={mutationLoading}
          {...register('name')}
          error={formState.errors.name?.message?.toString()}
          required
        />

        <div className="flex items-center w-full gap-2">
          <Select disabled={mutationLoading} label="report.priority" {...register('priority')}>
            {values(REPORT_PRIORITY).map((priority) => (
              <option value={priority} key={priority}>
                {t(`priority.${priority}`)}
              </option>
            ))}
          </Select>

          <Select disabled={mutationLoading} label="report.env" {...register('env')}>
            {values(REPORT_ENV).map((env) => (
              <option value={env} key={env}>
                {t(`environment.${env}`)}
              </option>
            ))}
          </Select>

          <Select disabled={mutationLoading} label="report.platform" {...register('platform')}>
            {values(REPORT_PLATFORM).map((platform) => (
              <option value={platform} key={platform}>
                {t(`platform.${platform}`)}
              </option>
            ))}
          </Select>
        </div>

        {platform === REPORT_PLATFORM.WEB && (
          <div className="grid">
            <span className="text-label required">{t('report.browsers')}</span>
            {values(BROWSERS).map((browser) => (
              <Checkbox
                disabled={mutationLoading}
                key={browser}
                label={browser === BROWSERS.OTHER ? t('common.other') : browser}
                {...register('browsers')}
                value={browser}
              />
            ))}
            <span className="error">{t(formState.errors.browsers?.message?.toString() ?? '')}</span>
            {!!browsers?.includes(BROWSERS.OTHER) && (
              <Input
                disabled={mutationLoading}
                label="common.other"
                placeholder="browser"
                type="text"
                {...register('otherBrowsers')}
                error={formState.errors.otherBrowsers?.message?.toString()}
                required
              />
            )}
          </div>
        )}

        {platform === REPORT_PLATFORM.MOBILE && (
          <>
            <Input
              disabled={mutationLoading}
              label="report.device"
              placeholder="device"
              type="text"
              {...register('device')}
              error={formState.errors.device?.message?.toString()}
              required
            />
            <div className="flex items-center gap-2">
              <Input
                disabled={mutationLoading}
                label="report.version"
                type="text"
                {...register('version')}
                error={formState.errors.version?.message?.toString()}
                required
              />
              <i
                ref={setTrigger}
                className="ri-information-line hover:text-primary-500 mt-[18px]"
                onMouseEnter={() => setShowHint(true)}
                onMouseLeave={() => setShowHint(false)}
              />
              {showHint && (
                <div
                  ref={setPopper}
                  style={styles.popper}
                  {...attributes.popper}
                  className="rounded py-4 px-6 bg-neutral-50 shadow-xl"
                >
                  {t('report.versionHint')}
                </div>
              )}
            </div>
          </>
        )}

        <h3 className="font-bold text-title pt-10">{t('report.currentBehavior')}</h3>
        <TextArea
          disabled={mutationLoading}
          label="report.description"
          {...register('currentDescription')}
          error={formState.errors.currentDescription?.message}
          required
        />
        <RichText
          label="report.reproduce"
          required
          disabled={mutationLoading}
          initialValue={reproduce}
          error={formState.errors.reproduce?.message}
          onChange={(v) => {
            setValue('reproduce', v.html, { shouldValidate: true });
          }}
        />
        <FileInput
          label="report.screenshots"
          disabled={mutationLoading}
          onChange={(files) => {
            setValue(
              'currentScreenshots',
              currentScreenshots ? [...currentScreenshots, ...files] : [...files],
            );
          }}
        />
        <FilePreview
          onRemoveFile={(files) => {
            resetField('currentScreenshots');
            setValue('currentScreenshots', files);
          }}
          files={currentScreenshots}
        />

        <h3 className="font-bold text-title pt-10">{t('report.expectedBehavior')}</h3>
        <TextArea
          disabled={mutationLoading}
          label="report.description"
          {...register('expectedDescription')}
          error={formState.errors.expectedDescription?.message}
          required
        />
        <FileInput
          disabled={mutationLoading}
          label="report.screenshots"
          onChange={(files) =>
            setValue(
              'expectedScreenshots',
              expectedScreenshots ? [...expectedScreenshots, ...files] : [...files],
            )
          }
        />
        <FilePreview
          onRemoveFile={(files) => setValue('expectedScreenshots', files)}
          files={expectedScreenshots}
        />

        <div className="flex justify-end items-center gap-2 mt-4">
          <button
            disabled={mutationLoading}
            className="primary"
            onClick={() => navigate(PATHS.homePath())}
            type="button"
          >
            {t('common.cancel')}
          </button>
          <button
            className="primary-fill"
            type="submit"
            onClick={() => fillDescription()}
            disabled={!formState.isValid || mutationLoading}
          >
            {t('report.createReport')}
          </button>
        </div>
      </form>
      <CreateReportSuccessDialog />
      <CreateReportOverviewDialog onSubmit={(event) => saveReport(event)} />
    </main>
  );
}
